import React, { useMemo, useRef } from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { Input, Label } from "components/Form/FormikControls";
import { InlineButton } from "components/Form/FormikControls/Label";
import { InformationPanel } from "components/Layout";
import { Info } from "components/LotCard/Elements";

import {
  getBuyerInformationString,
  parseBuyerInformationString,
} from "lib/businesses";

import {
  getBidders,
  getBuyerWaysByBusinessId,
  selectBidderByBuyerInformation,
} from "selectors";

type BuyerWayFormValue = { id?: string; name?: string } | null;

interface QuickSellBidderFormValues {
  bidderNumber?: string | number | null;
  buyerId?: string | null;
  destinationPropertyId?: string | null;
  buyerWay?: BuyerWayFormValue;
}

interface QuickSellBidderNumberInputProps {
  disabled?: boolean;
  autoFocus?: boolean;
  inlineButton?: InlineButton | null;
  enableTemporaryBusinessCreation?: boolean;
  /** When true, resolve bidder number from buyer / way / PIC when they change. */
  setBidderNumberFromBuyerDetails?: boolean;
}

export const QuickSellBidderNumberInput = ({
  disabled = false,
  autoFocus = false,
  inlineButton = null,
  enableTemporaryBusinessCreation = false,
  setBidderNumberFromBuyerDetails = false,
}: QuickSellBidderNumberInputProps) => {
  const formikProps = useFormikContext<QuickSellBidderFormValues>();

  const bidderLookup = useSelector(getBidders);

  const { values, setFieldValue, touched } = formikProps;

  const { bidderNumber, buyerId, destinationPropertyId, buyerWay } = values;

  const bidder = useMemo((): BidderRegistration | undefined => {
    const list = Object.values(bidderLookup) as BidderRegistration[];
    return list.find(bidderEntry => {
      return (
        String(bidderEntry.registrationNumber) === String(bidderNumber) &&
        !!bidderEntry.businessId
      );
    });
  }, [bidderLookup, bidderNumber]);

  const buyerInformationString = getBuyerInformationString(
    buyerId,
    destinationPropertyId,
    buyerWay?.name,
  );
  const bidderByBuyerInformationLookup = useSelector(
    selectBidderByBuyerInformation,
  );
  const existingBidder = bidderByBuyerInformationLookup[buyerInformationString];

  const showUpdateBidderNote =
    existingBidder &&
    String(existingBidder.registrationNumber) !== String(bidderNumber);

  const showCreateBidderNote =
    !disabled && !existingBidder && !!bidderNumber && touched.bidderNumber;

  const buyerWays =
    useSelector(getBuyerWaysByBusinessId(bidder?.businessId)) || [];

  const existingBuyerWay = buyerWays.find(bw => bw.name === bidder?.buyerWay);

  // Tracks the last bidder number that was successfully resolved to a bidder
  // registration. Used to distinguish "bidder data still loading" from "user
  // changed to an invalid bidder number" in the effect below.
  const lastResolvedBidderNumberRef =
    useRef<QuickSellBidderFormValues["bidderNumber"]>(null);

  React.useEffect(() => {
    // Derive individual buyer fields from the dep rather than from closed-over
    // variables, eliminating stale closure risk without needing an eslint-disable.
    const [buyerIdValue, destinationPropertyIdValue, buyerWayNameValue] =
      parseBuyerInformationString(buyerInformationString);

    if (!disabled) {
      // Bidder mode: update buyer fields from bidder number only.
      if (bidderNumber == null || bidderNumber === "") return;
      if (bidder?.businessId) {
        lastResolvedBidderNumberRef.current = bidderNumber;
        // If the buyer/way is already set to this bidder's business and way, the
        // form was pre-populated from a saved lot — don't overwrite a saved PIC.
        // Checking business alone isn't enough: two bidder registrations can
        // share a business but point at different buyer ways.
        // But if destinationPropertyId is still null, set it from the bidder's
        // default so the existingBidder lookup resolves to the correct bidder.
        if (
          buyerIdValue === bidder.businessId &&
          (buyerWayNameValue || null) === (bidder.buyerWay || null)
        ) {
          if (destinationPropertyIdValue == null) {
            setFieldValue(
              "destinationPropertyId",
              bidder.defaultPropertyId ?? null,
            );
          }
          return;
        }
        setFieldValue("buyerId", bidder.businessId);
        setFieldValue(
          "buyerWay",
          existingBuyerWay
            ? { id: existingBuyerWay.id, name: existingBuyerWay.name }
            : bidder.buyerWay
              ? { name: bidder.buyerWay }
              : null,
        );
        setFieldValue(
          "destinationPropertyId",
          bidder.defaultPropertyId ?? null,
        );
      } else if (
        lastResolvedBidderNumberRef.current != null &&
        lastResolvedBidderNumberRef.current !== bidderNumber
      ) {
        // Bidder number has changed away from a previously-resolved value and
        // no match exists — clear buyer fields.
        // We do NOT clear when lastResolved is null (never found a bidder yet)
        // because the lookup data might simply not have loaded yet.
        lastResolvedBidderNumberRef.current = null;
        setFieldValue("buyerId", null);
        setFieldValue("buyerWay", null);
        setFieldValue("destinationPropertyId", null);
      }
      return;
    }

    // Buyer mode: update bidder number from buyer fields only.
    if (!setBidderNumberFromBuyerDetails) {
      return;
    }

    if (
      existingBidder &&
      String(existingBidder.registrationNumber) !== String(bidderNumber)
    ) {
      setFieldValue("bidderNumber", existingBidder.registrationNumber);
      return;
    }

    if (
      (buyerIdValue || destinationPropertyIdValue || buyerWayNameValue) &&
      !existingBidder &&
      bidderNumber != null
    ) {
      setFieldValue("bidderNumber", null);
    }
  }, [
    disabled,
    bidder,
    existingBuyerWay,
    bidderNumber,
    setBidderNumberFromBuyerDetails,
    existingBidder,
    buyerInformationString,
    setFieldValue,
  ]);

  return (
    <>
      <Label inlineButton={inlineButton}>Bidder Number</Label>
      <Input
        name="bidderNumber"
        disabled={disabled}
        autoFocus={autoFocus}
        type="number"
      />
      {!enableTemporaryBusinessCreation && showUpdateBidderNote && (
        <Info className="p-1">
          <em>
            Note: Setting a new bidder number will update the current bidder
            registration number for this Buyer/Way/PIC combination
          </em>
        </Info>
      )}
      {enableTemporaryBusinessCreation && showCreateBidderNote && (
        <Grid item xs={12}>
          <InformationPanel className="mt-8">
            You&apos;ve entered a new Bidder Number. <br /> A temporary Business
            will be created with a bidder registration on Save. You can update
            this Business later.
          </InformationPanel>
        </Grid>
      )}
    </>
  );
};
